/**
 * Archivio: tutti gli allegati mai ricevuti, in un posto solo.
 *
 * Si riempie da solo: nessuno deve caricare o ordinare niente. La ricerca
 * normale filtra per nome, mittente e periodo; quella in linguaggio naturale
 * cerca nel senso delle email, così si trova un file anche quando si ricorda
 * di cosa parlava ma non come si chiamava.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  Archive as ArchiveIcon, Search, Sparkles, Download, Loader2, FileText,
  Image as ImageIcon, Sheet, FileArchive, File, X, Mail,
} from 'lucide-react';
import { InboxSidebar } from '@/components/layout/InboxSidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { archiveApi, attachmentsApi, type ArchiveItem } from '@/lib/api/endpoints';
import { API_URL, tokenStore } from '@/lib/api/client';
import { cn } from '@/lib/utils';

const KIND_META: Record<ArchiveItem['kind'], { label: string; icon: typeof File; cls: string }> = {
  pdf: { label: 'PDF', icon: FileText, cls: 'bg-rose-50 text-rose-600' },
  documento: { label: 'Documenti', icon: FileText, cls: 'bg-blue-50 text-blue-600' },
  foglio: { label: 'Fogli', icon: Sheet, cls: 'bg-emerald-50 text-emerald-600' },
  immagine: { label: 'Immagini', icon: ImageIcon, cls: 'bg-amber-50 text-amber-600' },
  archivio: { label: 'Archivi', icon: FileArchive, cls: 'bg-violet-50 text-violet-600' },
  altro: { label: 'Altro', icon: File, cls: 'bg-muted text-muted-foreground' },
};

function human(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

async function download(id: string, filename: string) {
  const url = `${API_URL.replace(/\/api\/v1$/, '')}${attachmentsApi.downloadUrl(id)}`;
  try {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${tokenStore.getAccess()}` } });
    if (!res.ok) throw new Error('Download non riuscito');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(await res.blob());
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  } catch (err) {
    toast.error(err instanceof Error ? err.message : 'Download non riuscito');
  }
}

export function ArchivePage() {
  const [text, setText] = useState('');
  const [kind, setKind] = useState<string | undefined>();
  const [contact, setContact] = useState<string | undefined>();
  const [smart, setSmart] = useState<string | null>(null); // domanda in linguaggio naturale

  const statsQuery = useQuery({ queryKey: ['archive-stats'], queryFn: () => archiveApi.stats() });

  const listQuery = useQuery({
    queryKey: ['archive', text, kind, contact],
    queryFn: () => archiveApi.list({ q: text || undefined, kind, contact }),
    enabled: !smart,
  });

  const smartQuery = useQuery({
    queryKey: ['archive-smart', smart],
    queryFn: () => archiveApi.search(smart!),
    enabled: !!smart,
  });

  const items = smart ? smartQuery.data?.items ?? [] : listQuery.data?.items ?? [];
  const loading = smart ? smartQuery.isLoading : listQuery.isLoading;
  const stats = statsQuery.data;

  function askAi() {
    const q = text.trim();
    if (q.length < 3) {
      toast.info('Scrivi cosa stai cercando, anche a parole tue');
      return;
    }
    setSmart(q);
  }

  function clearAll() {
    setSmart(null); setText(''); setKind(undefined); setContact(undefined);
  }

  const filtered = !!(smart || kind || contact || text);

  return (
    <div className="h-screen flex bg-background">
      <InboxSidebar />
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-8 py-8 space-y-6">

          <header className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0">
              <ArchiveIcon className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold leading-tight">Archivio</h1>
              <p className="text-sm text-muted-foreground mt-1 max-w-prose">
                Ogni allegato che hai ricevuto, raccolto qui senza che tu debba salvarlo.
                {stats && stats.total > 0 && (
                  <> Al momento <strong>{stats.total} file</strong> per {human(stats.bytes)}.</>
                )}
              </p>
            </div>
          </header>

          {/* ricerca */}
          <div className="space-y-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={text}
                  onChange={(e) => { setText(e.target.value); setSmart(null); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && e.metaKey) askAi(); }}
                  placeholder="Nome del file, mittente, oggetto…"
                  className="pl-9"
                />
              </div>
              <Button variant="outline" onClick={askAi} title="Cerca per significato, non per parole esatte">
                <Sparkles className="h-4 w-4 mr-1.5 text-indigo-500" /> Chiedi all'AI
              </Button>
            </div>

            {smart && (
              <div className="flex items-center gap-2 text-sm rounded-lg bg-indigo-50 px-3 py-2">
                <Sparkles className="h-4 w-4 text-indigo-500 shrink-0" />
                <span className="flex-1">Risultati per significato: <strong>{smart}</strong></span>
                <button onClick={clearAll} className="text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* filtri per tipo */}
            {!smart && stats && stats.kinds.length > 1 && (
              <div className="flex flex-wrap gap-1.5">
                {stats.kinds.map((k) => {
                  const meta = KIND_META[k.kind];
                  const on = kind === k.kind;
                  return (
                    <button
                      key={k.kind}
                      onClick={() => setKind(on ? undefined : k.kind)}
                      className={cn(
                        'text-xs rounded-full px-3 py-1.5 border transition-colors',
                        on ? 'bg-indigo-600 text-white border-indigo-600' : 'hover:bg-muted'
                      )}
                    >
                      {meta.label} <span className={on ? 'text-white/70' : 'text-muted-foreground'}>{k.n}</span>
                    </button>
                  );
                })}
                {filtered && (
                  <button onClick={clearAll} className="text-xs px-3 py-1.5 text-muted-foreground hover:text-foreground">
                    Azzera
                  </button>
                )}
              </div>
            )}

            {/* filtro per mittente */}
            {!smart && stats && stats.contacts.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {stats.contacts.slice(0, 6).map((c) => {
                  const on = contact === c.email;
                  return (
                    <button
                      key={c.email}
                      onClick={() => setContact(on ? undefined : c.email)}
                      className={cn(
                        'text-xs rounded-full px-3 py-1.5 border transition-colors max-w-[220px] truncate',
                        on ? 'bg-foreground text-background border-foreground' : 'hover:bg-muted'
                      )}
                      title={c.email}
                    >
                      {c.name || c.email} <span className="opacity-60">{c.n}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* risultati */}
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-10">
              <Loader2 className="h-4 w-4 animate-spin" /> {smart ? 'Sto cercando per significato…' : 'Carico l\'archivio…'}
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-xl border border-dashed py-14 text-center">
              <ArchiveIcon className="h-7 w-7 mx-auto text-muted-foreground/50" />
              <p className="mt-3 text-sm font-medium">
                {filtered ? 'Nessun file corrisponde' : 'Ancora nessun allegato'}
              </p>
              <p className="mt-1 text-sm text-muted-foreground max-w-sm mx-auto">
                {filtered
                  ? 'Prova con parole diverse, oppure chiedi all\'AI descrivendo di cosa parlava l\'email.'
                  : 'Si riempirà da solo: ogni allegato che ricevi finisce qui, senza che tu debba salvarlo.'}
              </p>
              {filtered && <Button variant="outline" size="sm" className="mt-4" onClick={clearAll}>Azzera i filtri</Button>}
            </div>
          ) : (
            <div className="space-y-1.5">
              {items.map((it) => <Row key={`${it.id}-${it.message_id}`} item={it} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ item }: { item: ArchiveItem }) {
  const meta = KIND_META[item.kind] ?? KIND_META.altro;
  const Icon = meta.icon;
  const from = item.from_address?.name || item.from_address?.email || '';

  return (
    <div className="rounded-xl border p-3 flex items-center gap-3 hover:bg-muted/40 transition-colors">
      <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center shrink-0', meta.cls)}>
        <Icon className="h-5 w-5" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="font-medium truncate">{item.filename}</div>
        <div className="text-sm text-muted-foreground truncate">
          {from}
          {item.received_at && ` · ${format(new Date(item.received_at), 'd MMM yyyy', { locale: it })}`}
          {item.size_bytes ? ` · ${human(item.size_bytes)}` : ''}
        </div>
        {item.subject && (
          <div className="text-xs text-muted-foreground/80 truncate mt-0.5 flex items-center gap-1">
            <Mail className="h-3 w-3 shrink-0" /> {item.subject}
          </div>
        )}
        {item.doc_type && (
          <div className="text-xs mt-1">
            <span className="rounded bg-indigo-50 text-indigo-700 px-1.5 py-0.5 font-medium">
              {item.doc_type}
              {item.doc_number ? ` n. ${item.doc_number}` : ''}
              {item.amount ? ` · ${Number(item.amount).toLocaleString('it-IT', { style: 'currency', currency: item.currency || 'EUR' })}` : ''}
            </span>
          </div>
        )}
      </div>

      <Button variant="ghost" size="sm" onClick={() => void download(item.id, item.filename)} title="Scarica">
        <Download className="h-4 w-4" />
      </Button>
    </div>
  );
}
